import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Send, Sparkles, ArrowLeft, MessagesSquare, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { FullSpinner, Empty } from "@/components/States";
import { toast } from "sonner";

function activeLabel(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 60000;
  if (diff < 15) return "Online now";
  if (diff < 1440) return "Active recently";
  return "Offline";
}

export default function Messages() {
  const { otherId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [convs, setConvs] = useState(null);

  useEffect(() => { api.get("/conversations").then(({ data }) => setConvs(data.conversations)).catch(() => setConvs([])); }, [otherId]);

  return (
    <div className="h-[calc(100vh-130px)] lg:h-[calc(100vh-100px)]" data-testid="messages-page">
      <div className="grid lg:grid-cols-3 gap-4 h-full">
        <div className={`mj-card overflow-hidden flex-col ${otherId ? "hidden lg:flex" : "flex"} lg:col-span-1`}>
          <div className="p-4 border-b border-[#F1E9DA]"><h1 className="font-serif text-xl font-bold text-[#58111A]">Messages</h1></div>
          <div className="flex-1 overflow-y-auto">
            {convs === null ? <FullSpinner /> : convs.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#605255]">No conversations yet.<br />Chat unlocks after an interest is accepted.</div>
            ) : convs.map((c) => (
              <button key={c.id} onClick={() => nav(`/app/messages/${c.other.user_id}`)} data-testid={`conv-${c.other.user_id}`}
                className={`w-full flex items-center gap-3 p-3 border-b border-[#F1E9DA] text-left hover:bg-[#FAF5EE] ${otherId === c.other.user_id ? "bg-[#FAF3E0]" : ""}`}>
                <Avatar photos={c.other.photos} name={c.other.full_name} size={44} className="rounded-full shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between"><span className="font-semibold text-sm text-[#261A1C] truncate">{c.other.full_name}</span>{c.unread > 0 && <span className="bg-[#BE123C] text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{c.unread}</span>}</div>
                  <div className="text-xs text-[#605255] truncate">{c.last_message}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className={`lg:col-span-2 ${otherId ? "block" : "hidden lg:block"} h-full`}>
          {otherId ? <ChatThread otherId={otherId} me={user.id} /> : (
            <div className="mj-card h-full flex items-center justify-center">
              <Empty icon={MessagesSquare} title="Select a conversation" message="Start a conversation with one of your compatible matches." />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatThread({ otherId, me }) {
  const nav = useNavigate();
  const [state, setState] = useState({ messages: [], other: null, connected: true });
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [starters, setStarters] = useState([]);
  const [starterBusy, setStarterBusy] = useState(false);
  const endRef = useRef(null);

  const load = () => api.get(`/messages/${otherId}`).then(({ data }) => setState(data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [otherId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [state.messages]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try { await api.post("/messages", { to_user: otherId, text }); setText(""); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const getStarters = async () => {
    setStarterBusy(true);
    try { const { data } = await api.get(`/ai/conversation-starters/${otherId}`); setStarters(data.starters); }
    catch (e) { toast.error(apiError(e)); } finally { setStarterBusy(false); }
  };

  return (
    <div className="mj-card h-full flex flex-col" data-testid="chat-thread">
      <div className="p-3 border-b border-[#F1E9DA] flex items-center gap-3">
        <button className="lg:hidden" onClick={() => nav("/app/messages")}><ArrowLeft className="w-5 h-5 text-[#58111A]" /></button>
        {state.other && <><Avatar photos={state.other.photos} name={state.other.full_name} size={40} className="rounded-full" />
          <div className="flex-1 cursor-pointer" onClick={() => nav(`/app/profile/${otherId}`)}>
            <div className="font-semibold text-sm text-[#261A1C]">{state.other.full_name}</div>
            <div className="text-xs text-emerald-600">{activeLabel(state.other.last_active)}</div>
          </div></>}
        <button onClick={getStarters} disabled={starterBusy} data-testid="ai-starters-btn" className="btn-outline text-xs">{starterBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5" /> Suggestions</>}</button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#FDFBF7]">
        {state.messages.length === 0 && <p className="text-center text-sm text-[#9E7B1B] mt-6">Say hello and start the conversation.</p>}
        {state.messages.map((m) => (
          <div key={m.id} className={`flex ${m.from_user === me ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm ${m.from_user === me ? "bg-[#781826] text-white rounded-br-sm" : "bg-white border border-[#E8DFD1] text-[#261A1C] rounded-bl-sm"}`}>
              {m.text}
              <div className={`text-[10px] mt-1 ${m.from_user === me ? "text-[#E7CDCF]" : "text-[#9E7B1B]"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}{m.from_user === me && (m.read ? " · Read" : " · Sent")}</div>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {starters.length > 0 && (
        <div className="px-3 py-2 border-t border-[#F1E9DA] flex gap-2 overflow-x-auto hide-scrollbar">
          {starters.map((s, i) => <button key={i} onClick={() => { setText(s); setStarters([]); }} className="chip shrink-0 !whitespace-normal text-left">{s}</button>)}
        </div>
      )}

      <div className="p-3 border-t border-[#F1E9DA] flex gap-2">
        <input data-testid="chat-input" className="mj-input" placeholder={state.connected ? "Type a message…" : "Connect first to chat"} value={text} disabled={!state.connected} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
        <button data-testid="chat-send" onClick={send} disabled={sending || !state.connected} className="btn-primary shrink-0">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
      </div>
    </div>
  );
}
